import { memo } from 'react';

import { useAnalyticsDebug } from '@air/analytics';
import { usePlayerDebug } from '@air/component-player';
import {
  ArrowLeftRightIcon,
  CheckIcon,
  PlusIcon,
  RepeatIcon,
  SearchIcon,
  WandIcon,
  WandSparklesIcon,
} from '@air/design-icons';
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
} from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';
import { useSysadmin } from '@air/sysadmin';

import { useDevModal } from '~/components/DevModal/hooks/useDevModal';
import { InternalBrowseWorkspaceModal } from '~/components/v2/components/internal/modals/internal-browse-workspace-modal/InternalBrowseWorkspaceModal';
import { useDevToolsStore } from '~/components/v2/store/features/devtools/useDevToolsStore';
import { useCreateWorkspace } from '~/hooks/useCreateWorkspace';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';

export const WorkspaceHeaderInformationDropdownDevToolsItems = memo(() => {
  const { ui2026 } = useTemporaryFeatureFlags();
  const workspaceIdVisible = useDevToolsStore((store) => store.workspaceIdVisible);
  const toggleWorkspaceIdVisible = useDevToolsStore((store) => store.toggleWorkspaceIdVisible);
  const reactQueryDevtoolsVisible = useDevToolsStore((store) => store.reactQueryDevtoolsVisible);
  const toggleReactQueryDevtoolsVisible = useDevToolsStore((store) => store.toggleReactQueryDevtoolsVisible);
  const nodeInspectorVisible = useDevToolsStore((store) => store.nodeInspectorVisible);
  const toggleNodeInspectorVisible = useDevToolsStore((store) => store.toggleNodeInspectorVisible);
  const { handleClearWorkspaceId, isInternal } = useSysadmin();
  const { value: analyticsDebugValue, onToggle: onToggleAnalyticsDebug } = useAnalyticsDebug();
  const { value: playerDebugValue, onToggle: onTogglePlayerDebug } = usePlayerDebug();
  const [showBrowseWorkspaceModal] = useAirModal(InternalBrowseWorkspaceModal);
  const { openDevModal, shouldShowDevButton } = useDevModal();
  const { createWorkspace } = useCreateWorkspace();

  return (
    <>
      <DropdownMenuSeparator />
      <div className="-m-2 flex flex-col gap-2 ui2026:rounded-b-8 ads:bg-base bg-gray-1 p-2">
        <DropdownMenuLabel className="ui2026:hidden">Internal</DropdownMenuLabel>

        {isInternal && (
          <DropdownMenuItem
            label="Switch Workspace"
            prefix={
              ui2026 ? (
                <ArrowLeftRightIcon aria-hidden className="ads:text-icon-subtle text-gray-9" size="sm" />
              ) : (
                <RepeatIcon size="sm" />
              )
            }
            onClick={handleClearWorkspaceId}
          />
        )}

        <DropdownMenuItem
          label="Browse workspaces"
          onSelect={showBrowseWorkspaceModal}
          prefix={
            ui2026 ? (
              <SearchIcon aria-hidden className="ads:text-icon-subtle text-gray-9" size="sm" />
            ) : (
              <SearchIcon size="sm" />
            )
          }
        />
        {isInternal && (
          <DropdownMenuItem
            label="Create workspace"
            onSelect={createWorkspace}
            prefix={
              ui2026 ? (
                <PlusIcon aria-hidden className="ads:text-icon-subtle text-gray-9" size="sm" />
              ) : (
                <PlusIcon size="sm" />
              )
            }
          />
        )}

        <DropdownMenuSub
          className="ads:bg-base bg-gray-1"
          label="DevTools"
          prefix={
            ui2026 ? (
              <WandSparklesIcon aria-hidden className="ads:text-icon-subtle text-gray-9" size="sm" />
            ) : (
              <WandIcon size="sm" />
            )
          }
        >
          {shouldShowDevButton && (
            <DropdownMenuItem
              description="Open the developer modal for debugging and testing"
              label="Dev Modal"
              onSelect={openDevModal}
            />
          )}
          <DropdownMenuItem
            description="Toggle Workspace ID visibility in the workspace switcher UI"
            label="Workspace ID"
            onSelect={toggleWorkspaceIdVisible}
            suffix={workspaceIdVisible ? <CheckIcon size="sm" /> : null}
          />
          <DropdownMenuItem
            description="Logs the analytics events to the console for debugging purposes"
            label="Analytics"
            onSelect={onToggleAnalyticsDebug}
            suffix={analyticsDebugValue ? <CheckIcon size="sm" /> : null}
          />
          <DropdownMenuItem
            description="Logs the player events to the console for debugging purposes"
            label="Player"
            onSelect={onTogglePlayerDebug}
            suffix={playerDebugValue ? <CheckIcon size="sm" /> : null}
          />
          <DropdownMenuItem
            description="Toggle canvas node inspector panel visibility"
            label="Canvas Node Inspector"
            onSelect={toggleNodeInspectorVisible}
            suffix={nodeInspectorVisible ? <CheckIcon size="sm" /> : null}
          />
          <DropdownMenuItem
            description="Toggle React Query devtools visibility"
            label="React Query"
            onSelect={toggleReactQueryDevtoolsVisible}
            suffix={reactQueryDevtoolsVisible ? <CheckIcon size="sm" /> : null}
          />
        </DropdownMenuSub>
      </div>
    </>
  );
});

WorkspaceHeaderInformationDropdownDevToolsItems.displayName = 'WorkspaceHeaderInformationDropdownDevToolsItems';
